import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, Sparkles, ArrowRight, Briefcase, Users, BookOpen, Compass } from 'lucide-react';
import { useT } from '../i18n';

const STAGES = [
  { id: 'after-10th',  titleK: 'onboarding.s_after_10th_t',  subK: 'onboarding.s_after_10th_s',  icon: GraduationCap, color: 'from-blue-500 to-indigo-500' },
  { id: 'after-inter', titleK: 'onboarding.s_after_inter_t', subK: 'onboarding.s_after_inter_s', icon: BookOpen,      color: 'from-emerald-500 to-teal-500' },
  { id: 'college',     titleK: 'onboarding.s_college_t',     subK: 'onboarding.s_college_s',     icon: Compass,       color: 'from-purple-500 to-pink-500' },
  { id: 'final-year',  titleK: 'onboarding.s_final_t',       subK: 'onboarding.s_final_s',       icon: Sparkles,      color: 'from-amber-500 to-orange-500' },
  { id: 'working',     titleK: 'onboarding.s_working_t',     subK: 'onboarding.s_working_s',     icon: Briefcase,     color: 'from-rose-500 to-red-500' },
  { id: 'parent',      titleK: 'onboarding.s_parent_t',      subK: 'onboarding.s_parent_s',      icon: Users,         color: 'from-cyan-500 to-blue-500' },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const OnboardingStagePage = () => {
  const navigate = useNavigate();
  const { t } = useT();
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      await axios.post(`${API}/lockdown/stage`, { stage: picked });
      navigate('/');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 flex flex-col">
      <header className="px-4 md:px-6 py-4 border-b border-gray-100 bg-white/70 backdrop-blur">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-xl md:text-2xl font-black text-teal-700" data-testid="onboarding-logo">LearnixTa</Link>
          <span className="text-xs text-gray-500 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" /> {t('onboarding.step_label')}
          </span>
        </div>
      </header>

      <main className="flex-1 px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-5xl font-black text-gray-900 leading-[1.05] tracking-tight mb-3" data-testid="onboarding-title">
              {t('onboarding.headline')}
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {t('onboarding.sub')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {STAGES.map((s) => {
              const Icon = s.icon;
              const active = picked === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setPicked(s.id)}
                  data-testid={`stage-card-${s.id}`}
                  className={`text-left rounded-3xl p-5 md:p-6 transition-all border-2 ${
                    active
                      ? 'border-teal-500 bg-white shadow-xl shadow-teal-500/10 scale-[1.01]'
                      : 'border-transparent bg-white hover:border-teal-300 hover:shadow-md'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${s.color} text-white flex items-center justify-center mb-4`}>
                    <Icon size={20} />
                  </div>
                  <h2 className="text-base md:text-lg font-extrabold text-gray-900 leading-snug">{t(s.titleK)}</h2>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">{t(s.subK)}</p>
                </button>
              );
            })}
          </div>

          <div className="mt-10 flex flex-col items-center gap-2.5">
            <button
              type="button"
              disabled={!picked || busy}
              onClick={submit}
              data-testid="onboarding-cta-unlock"
              className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-extrabold px-7 py-3.5 rounded-full transition-colors"
            >
              {busy ? t('onboarding.cta_unlocking') : <>{t('onboarding.cta_unlock')} <ArrowRight size={16} /></>}
            </button>
            <p className="text-xs text-gray-500 text-center max-w-md">
              {picked ? t('onboarding.cta_help') : t('onboarding.cta_pick')}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OnboardingStagePage;
